/**
 * Centralized structured logger.
 *
 * Today this is a thin wrapper over `console` so we have ONE place to
 * swap in Sentry / Logtail / Datadog later without touching call sites.
 * Until a `SENTRY_DSN` (server) / `VITE_SENTRY_DSN` (browser) is set the
 * logger is a no-op aside from console output.
 *
 * Usage:
 *   import { logger } from "@/lib/logger";
 *   logger.error("packOrder failed", err, { orderId });
 *
 * Replace ALL ad-hoc `console.error` sites with `logger.error` so future
 * sink wiring is a one-line change in this file.
 */

type Extras = Record<string, unknown> | undefined;

type Sink = {
  captureException: (err: unknown, extras?: Extras) => void;
  captureMessage: (msg: string, extras?: Extras) => void;
};

let externalSink: Sink | null = null;

/**
 * Wire a remote sink (Sentry, Logtail, etc.) at app/server boot.
 * Safe to call multiple times — last writer wins.
 */
export function installLoggerSink(sink: Sink) {
  externalSink = sink;
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (typeof err === "string") return new Error(err);
  try {
    return new Error(JSON.stringify(err));
  } catch {
    return new Error(String(err));
  }
}

export const logger = {
  debug(msg: string, extras?: Extras) {
    if (typeof console !== "undefined") console.debug(`[debug] ${msg}`, extras ?? "");
  },
  info(msg: string, extras?: Extras) {
    if (typeof console !== "undefined") console.info(`[info] ${msg}`, extras ?? "");
  },
  warn(msg: string, extras?: Extras) {
    if (typeof console !== "undefined") console.warn(`[warn] ${msg}`, extras ?? "");
    externalSink?.captureMessage(msg, extras);
  },
  error(msg: string, err?: unknown, extras?: Extras) {
    const error = toError(err ?? msg);
    if (typeof console !== "undefined") console.error(`[error] ${msg}`, error, extras ?? "");
    externalSink?.captureException(error, { message: msg, ...(extras ?? {}) });
  },
};